import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for autosaving framework customization data
 * Provides visual feedback and error handling for save operations
 */
export const useFrameworkAutosave = (userId, debounceMs = 2000) => {
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const saveTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Save data to the database with visual feedback
   * @param {Object} data - The data to save
   * @param {boolean} immediate - Whether to save immediately without debouncing
   */
  const save = useCallback(async (data, immediate = false) => {
    if (!userId) {
      console.error('❌ No userId provided for autosave');
      setErrorMessage('No user ID available');
      setSaveStatus('error');
      return false;
    }

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const performSave = async () => {
      if (!isMountedRef.current) return;
      
      setSaveStatus('saving');
      setErrorMessage(null);
      
      try {
        console.log('💾 Autosaving framework data for user:', userId);
        console.log('📦 Data being saved:', data ? Object.keys(data) : 'no data');
        
        // Use API endpoint instead of direct Supabase call to bypass RLS issues
        console.log('🌐 Calling framework save API...');
        
        const response = await fetch('/api/framework/save-customization', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            data
          }),
        });

        const result = await response.json();
        
        console.log('📊 API response:', {
          status: response.status,
          success: result.success,
          message: result.message
        });

        if (!response.ok || !result.success) {
          console.error('❌ API call failed:', result);
          throw new Error(result.error || result.details || 'Framework save API failed');
        }
        
        console.log('✅ Framework data saved via API');
        console.log('📋 Updated fields:', result.updatedFields);
        console.log('🕒 Framework customized at:', result.frameworkCustomizedAt);

        if (isMountedRef.current) {
          setSaveStatus('saved');
          setLastSaved(new Date());
          console.log('✅ Autosave successful');
          
          // Reset status after 3 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setSaveStatus('idle');
            }
          }, 3000);
        }
        
        return true;
      } catch (error) {
        console.error('❌ Autosave failed:', error);
        console.error('❌ Error details:', {
          name: error.name,
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          stack: error.stack
        });
        if (isMountedRef.current) {
          setSaveStatus('error');
          setErrorMessage(error.message || 'Failed to save');
        }
        return false;
      }
    };

    if (immediate) {
      return performSave();
    } else {
      setSaveStatus('pending');
      saveTimeoutRef.current = setTimeout(performSave, debounceMs);
    }
  }, [userId, debounceMs]);

  /**
   * Manually trigger a save (useful for save buttons)
   */
  const saveNow = useCallback((data) => {
    return save(data, true);
  }, [save]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setErrorMessage(null);
    setSaveStatus('idle');
  }, []);

  return {
    saveStatus,
    lastSaved,
    errorMessage,
    save,
    saveNow,
    clearError,
    isSaving: saveStatus === 'saving',
    hasSaved: saveStatus === 'saved',
    hasError: saveStatus === 'error',
    isPending: saveStatus === 'pending'
  };
};

/**
 * Component to display save status
 */
export const SaveStatusIndicator = ({ status, errorMessage, lastSaved }) => {
  const getStatusDisplay = () => {
    switch (status) {
      case 'pending':
        return (
          <span className="text-yellow-600 flex items-center gap-1">
            <span className="w-2 h-2 bg-yellow-600 rounded-full animate-pulse" />
            Changes pending...
          </span>
        );
      case 'saving':
        return (
          <span className="text-azure-600 flex items-center gap-1">
            <svg className="animate-spin h-4 w-4 text-azure-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Saving...
          </span>
        );
      case 'saved':
        return (
          <span className="text-emerald-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Saved
            {lastSaved && (
              <span className="text-xs text-slate-500 ml-1">
                ({new Date(lastSaved).toLocaleTimeString()})
              </span>
            )}
          </span>
        );
      case 'error':
        return (
          <span className="text-red-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {errorMessage || 'Failed to save'}
          </span>
        );
      default:
        return null;
    }
  };

  const display = getStatusDisplay();
  if (!display) return null;

  return (
    <div className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-slate-200 text-sm">
      {display}
    </div>
  );
};